// The identity a flag evaluation is bucketed and targeted against: entityId feeds Flipt's deterministic
// percentage hashing (entityId + flagKey), context carries attributes segment rules match on.
export interface FlagIdentity {
  entityId: string;
  context: Record<string, string>;
}

const DEVICE_ID_STORAGE_KEY = "flagDeviceId";

// Segment rules that target individual players match on this context key (value = userUid). It is the
// documented operator contract, so the string must not drift.
const USER_CONTEXT_KEY = "userId";

// Segment rules that target a VIP tier match on this context key with a numeric constraint (e.g.
// vipLevel gte 3). The backend sends the same key for the same player, so the string must not drift.
const VIP_LEVEL_CONTEXT_KEY = "vipLevel";

// crypto.randomUUID is absent in non-secure contexts and some test (jsdom) environments; fall back to a
// random string so a device id is always mintable synchronously.
function mintDeviceId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `dev-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

// A stable per-device id for anonymous visitors: percentage bucketing is deterministic only against a
// fixed entityId, so the id is persisted to survive reloads. Self-minted and synchronous — unlike the
// async, late-loading fingerprint the app mints elsewhere, which would leave early evaluations unbucketed.
export function getOrCreateDeviceId(): string {
  const existing = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (existing) {
    return existing;
  }
  const minted = mintDeviceId();
  localStorage.setItem(DEVICE_ID_STORAGE_KEY, minted);
  return minted;
}

// A logged-in player buckets on their userUid and is targetable by segment rules via the userId context
// attribute, plus vipLevel once their level is known; an anonymous visitor buckets on the persisted
// device id and carries no context. An unknown level is left out rather than sent as 0, so a VIP
// segment never matches a player whose level has not loaded.
export function resolveFlagIdentity(userUid?: string, vipLevel?: number): FlagIdentity {
  if (userUid) {
    const context: Record<string, string> = { [USER_CONTEXT_KEY]: userUid };
    if (vipLevel !== undefined) {
      context[VIP_LEVEL_CONTEXT_KEY] = String(vipLevel);
    }
    return { entityId: userUid, context };
  }
  return { entityId: getOrCreateDeviceId(), context: {} };
}

// The synchronous bridge so the non-React IOC singleton can read the same identity the React provider
// maintains: the provider pushes each resolved identity here and the singleton reads it back, without
// giving the singleton Redux access. Defaults to the anonymous identity so the singleton is valid even
// before the provider mounts.
let currentIdentity: FlagIdentity = resolveFlagIdentity(undefined);

export function setFlagIdentity(identity: FlagIdentity): void {
  currentIdentity = identity;
}

export function getFlagIdentity(): FlagIdentity {
  return currentIdentity;
}
